/**
 * SelectionsReview — the schedules on the Selections Package card, answered LINE BY LINE (Matt,
 * 2026-10-26: "a line-by-line approval process and not an approve-all-at-once step"). Each item is
 * Approved, Not approved, or Change or discuss (with a note); every answer is saved at once and records
 * who gave it (api/journey-respond.js kind 'selection_item' → selection_item_decisions). When every item
 * has an answer the client sends them: kind 'approve' when all are approved, else 'request_changes'
 * (api/_selectionsReview.js posts the client review and turns the rest into one thread).
 *
 * entry.schedule.items = the issued schedule (api/_shareDocs.js#fetchScheduleReleaseForMilestone);
 * entry.itemDecisions = { [itemId]: { status, note, decided_by_name, decided_at } }, newest per item.
 */

import { useState } from 'react'
import { createPortal } from 'react-dom'
import { J, PrimaryButton, GhostButton, DoneChip } from '../journeyUI.jsx'
import { SCHEDULE_TYPES, scheduleSpecFor, scheduleCell } from '../../services/scheduleLayout.js'
import { decideSelectionItem } from '../../services/journey.js'

const INK = '#07272D', MUTED = '#7a9aaa'
const CHOICES = [
  { status: 'approved', label: 'Approve', mark: '✓', color: '#2e7d4f', bg: 'rgba(46,125,79,0.10)' },
  { status: 'not_approved', label: 'Not approved', mark: '✕', color: '#b03a2e', bg: 'rgba(176,58,46,0.08)' },
  { status: 'discuss', label: 'Change or discuss', mark: '?', color: '#b9770e', bg: 'rgba(185,119,14,0.10)' },
]
const BY = Object.fromEntries(CHOICES.map(c => [c.status, c]))
const shortDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : ''

function groupByType(items) {
  const by = {}
  for (const it of (items || [])) (by[it.schedule_type] = by[it.schedule_type] || []).push(it)
  const ordered = SCHEDULE_TYPES.filter(t => by[t]).concat(Object.keys(by).filter(t => !SCHEDULE_TYPES.includes(t)))
  return ordered.map(t => [t, by[t]])
}

/* The picture and the links a schedule item carries (Matt, 2026-10-26: "allow the user to expand the
   image and open any URL links provided in the schedules. URLs should open in new tabs"). The picture
   is the item's 'Image URL'; a link is any other value that is a web address ('Link', 'Electrical Link
   Override', or one typed into another column). */
const imageOf = (it) => { const a = it.attrs || {}; return a['Image URL'] || a.image_url || null }
const isUrl = (v) => typeof v === 'string' && /^https?:\/\/\S+$/i.test(v.trim())
function linksOf(it) {
  const a = it.attrs || {}, img = imageOf(it), seen = new Set(), out = []
  for (const [k, v] of Object.entries(a)) {
    if (k === 'Image URL' || k === 'image_url' || !isUrl(v) || v.trim() === img || seen.has(v.trim())) continue
    seen.add(v.trim())
    let host = ''
    try { host = new URL(v.trim()).hostname.replace(/^www\./, '') } catch (_) { /* label by key only */ }
    out.push({ url: v.trim(), label: /link/i.test(k) ? (host || 'Product page') : k + (host ? ' (' + host + ')' : '') })
  }
  return out
}

function Lightbox({ src, alt, onClose }) {
  return (
    <div onClick={onClose} onKeyDown={e => { if (e.key === 'Escape') onClose() }} tabIndex={-1} ref={el => el && el.focus()}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(7,39,45,0.86)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24, cursor: 'zoom-out', outline: 'none' }}>
      <img src={src} alt={alt} style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain', borderRadius: 6, boxShadow: '0 20px 60px rgba(0,0,0,0.4)', background: '#fff' }} />
      <button onClick={onClose} aria-label="Close"
        style={{ position: 'absolute', top: 16, right: 20, background: 'none', border: 'none', color: '#fff', fontSize: 28, cursor: 'pointer' }}>×</button>
    </div>
  )
}

function ItemRow({ it, decision, locked, onDecide }) {
  const [zoom, setZoom] = useState(false)
  const [open, setOpen] = useState(null)          // the status whose note box is open
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const spec = scheduleSpecFor(it.schedule_type)
  const sub = (spec.groupBy === 'spec_section' ? it.spec_section : it.room_string) || ''
  const hint = spec.columns[1] ? scheduleCell(it, spec.columns[1][1]) : ''
  const cur = decision && BY[decision.status]
  const img = imageOf(it), links = linksOf(it)

  async function save(status, n) {
    if (busy) return
    setBusy(true); setErr('')
    try { await onDecide(it, status, n); setOpen(null); setNote('') }
    catch (e) { setErr(e.message || 'That answer did not save. Please try again.') }
    finally { setBusy(false) }
  }
  function pick(c) {
    if (c.status === 'approved') return save('approved', '')
    setNote(decision && decision.status === c.status ? (decision.note || '') : '')
    setOpen(c.status)
  }

  return (
    <div style={{ padding: '12px 0', borderBottom: '0.5px solid rgba(7,39,45,0.08)' }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'flex-start' }}>
        {img && (
          <button onClick={() => setZoom(true)} title="See it larger"
            style={{ flexShrink: 0, width: 64, height: 64, padding: 0, border: '0.5px solid rgba(7,39,45,0.15)', borderRadius: 6, overflow: 'hidden', background: '#fff', cursor: 'zoom-in' }}>
            <img src={img} alt={it.item_label || ''} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          </button>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'baseline' }}>
            <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 14, color: INK }}>{it.item_label || '(unnamed)'}</span>
            <span style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: MUTED, textAlign: 'right' }}>{[sub, hint].filter(Boolean).join(' · ')}</span>
          </div>
          {links.length > 0 && (
            <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', marginTop: 4 }}>
              {links.map(l => (
                <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer"
                  style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#2b6c8a', textDecoration: 'underline' }}>{l.label} ↗</a>
              ))}
            </div>
          )}
        </div>
      </div>
      {zoom && <Lightbox src={img} alt={it.item_label || ''} onClose={() => setZoom(false)} />}
      {!locked && (
        <div style={{ display: 'flex', gap: 6, marginTop: 8, flexWrap: 'wrap' }}>
          {CHOICES.map(c => {
            const on = cur && cur.status === c.status
            return (
              <button key={c.status} disabled={busy} onClick={() => pick(c)}
                style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, fontWeight: 600, padding: '5px 12px', borderRadius: 20, cursor: busy ? 'default' : 'pointer',
                  border: '0.5px solid ' + (on ? c.color : 'rgba(7,39,45,0.2)'), background: on ? c.bg : '#fff', color: on ? c.color : INK }}>
                <span style={{ marginRight: 5 }}>{c.mark}</span>{c.label}
              </button>
            )
          })}
        </div>
      )}
      {open && (
        <div style={{ marginTop: 8 }}>
          <textarea value={note} onChange={e => setNote(e.target.value)} autoFocus
            placeholder={open === 'discuss' ? 'What would you like changed, or talked through?' : 'What is not right about it? (optional)'}
            style={{ width: '100%', minHeight: 64, padding: 10, borderRadius: 8, resize: 'vertical', border: '0.5px solid rgba(7,39,45,0.25)',
              fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: INK, outline: 'none', boxSizing: 'border-box' }} />
          <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
            <PrimaryButton disabled={busy || (open === 'discuss' && !note.trim())} onClick={() => save(open, note.trim())}>Save</PrimaryButton>
            <GhostButton disabled={busy} onClick={() => { setOpen(null); setNote('') }}>Cancel</GhostButton>
          </div>
        </div>
      )}
      {decision && !open && (
        <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: cur ? cur.color : MUTED, marginTop: 6 }}>
          {cur ? cur.mark + ' ' + cur.label : decision.status}
          <span style={{ color: MUTED }}> · {decision.decided_by_name || 'Client'}{decision.source === 'meeting' ? ', in our meeting' + (decision.meeting_date ? ' ' + shortDate(decision.meeting_date + 'T12:00:00') : '') : (decision.decided_at ? ', ' + shortDate(decision.decided_at) : '')}</span>
          {decision.note && <div style={{ color: INK, fontStyle: 'italic', marginTop: 3 }}>&ldquo;{decision.note}&rdquo;</div>}
        </div>
      )}
      {err && <div style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#b03a2e', marginTop: 6 }}>{err}</div>}
    </div>
  )
}

/* The card stays clean (Matt, 2026-10-26: "what if the input list was a popup from the schedules book
   instead of our 3D flip book"): the card shows one status line; opening the Schedules book, or the
   line's button, brings up the list in a popup, with Send at its foot and the printed PDF one link away. */
export default function SelectionsReview({ entry, response, onRespond, token, label, decide, open, onOpen, onClose, pdfUrl }) {
  const items = (entry.schedule && entry.schedule.items) || []
  const [decisions, setDecisions] = useState(() => ({ ...(entry.itemDecisions || {}) }))
  const [busy, setBusy] = useState(false)
  const sent = response && (response.kind === 'approve' || response.kind === 'request_changes')
  const answered = items.filter(it => decisions[it.id]).length
  const notOk = items.filter(it => decisions[it.id] && decisions[it.id].status !== 'approved').length

  async function onDecide(it, status, note) {
    const payload = { itemId: it.id, version: it.version, label: it.item_label, scheduleType: it.schedule_type, status, note }
    const r = decide ? await decide(payload) : await decideSelectionItem(token, entry.id, payload)
    setDecisions(d => ({ ...d, [it.id]: r.decision }))
  }
  async function send() {
    if (busy) return
    setBusy(true)
    try { await onRespond(notOk ? 'request_changes' : 'approve', {}) } finally { setBusy(false) }
  }

  if (!items.length) return null
  const sentChip = sent && (
    <DoneChip>Sent{response.created_at ? ' · ' + shortDate(response.created_at) : ''}: {items.length - notOk} approved{notOk ? ', ' + notOk + ' to change or discuss' : ''}</DoneChip>
  )

  const card = (
    <div style={{ textAlign: 'center', marginTop: 24 }}>
      {sent ? sentChip : (
        <>
          <div style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: MUTED, marginBottom: 10 }}>
            {answered ? `${answered} of ${items.length} selections answered` : `${items.length} selections for you to review`}
          </div>
          <PrimaryButton onClick={onOpen}>{answered ? 'Continue reviewing the selections' : 'Review the selections'}</PrimaryButton>
        </>
      )}
      {sent && <div style={{ marginTop: 10 }}><GhostButton onClick={onOpen}>See your answers</GhostButton></div>}
    </div>
  )

  return (
    <>
      {card}
      {open && createPortal(
        <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 950, background: 'rgba(7,39,45,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
          <div onClick={e => e.stopPropagation()} role="dialog" aria-label={label}
            style={{ background: '#fff', borderRadius: 14, width: 'min(760px, 100%)', maxHeight: '100%', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 60px rgba(7,39,45,0.3)' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12, padding: '22px 28px 12px', borderBottom: '0.5px solid rgba(7,39,45,0.1)' }}>
              <div style={{ flex: 1 }}>
                <div style={{ ...J.eyebrow, marginBottom: 4 }}>Schedules</div>
                <div style={{ fontFamily: '"Montserrat",sans-serif', fontSize: 16, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: INK }}>{label}</div>
                <div style={{ ...J.body, fontSize: 13, marginTop: 6 }}>
                  {sent ? 'Your answers on each selection.' : 'Go through each selection and let us know what you think of it. Your answers save as you go.'}
                </div>
              </div>
              <button onClick={onClose} aria-label="Close" style={{ background: 'none', border: 'none', fontSize: 26, lineHeight: 1, color: MUTED, cursor: 'pointer' }}>×</button>
            </div>
            <div style={{ overflowY: 'auto', padding: '6px 28px 10px' }}>
              {groupByType(items).map(([type, list]) => (
                <div key={type} style={{ marginTop: 14 }}>
                  <div style={{ fontFamily: '"Montserrat",sans-serif', fontSize: 12, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: INK, marginBottom: 2 }}>{type}</div>
                  {list.map((it, i) => <ItemRow key={it.id || i} it={it} decision={decisions[it.id]} locked={sent} onDecide={onDecide} />)}
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', padding: '14px 28px 20px', borderTop: '0.5px solid rgba(7,39,45,0.1)' }}>
              {pdfUrl && <a href={pdfUrl} target="_blank" rel="noopener noreferrer" style={{ fontFamily: '"DM Sans",sans-serif', fontSize: 12, color: '#2b6c8a' }}>The printed schedules (PDF) ↗</a>}
              <div style={{ flex: 1 }} />
              {sent ? sentChip : (
                <>
                  <span style={{ fontFamily: '"DM Mono",monospace', fontSize: 11, color: MUTED }}>{answered} of {items.length} answered</span>
                  <PrimaryButton disabled={busy || answered < items.length} onClick={send}>Send my answers to Alder James</PrimaryButton>
                </>
              )}
            </div>
            {sent && (
              <div style={{ ...J.body, fontSize: 13, color: MUTED, textAlign: 'center', padding: '0 28px 18px' }}>
                {notOk ? 'Thank you. We will work through the ones you marked and show you the update.' : 'Thank you. We will carry these selections into the construction documents.'}
              </div>
            )}
          </div>
        </div>, document.body
      )}
    </>
  )
}
